import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const NewsPage = () => {
  const { language } = useLanguage();
  const t = translations.news;

  // Track if mobile view
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const isEnMobile = language === 'en' && isMobile;
  const isZhMobile = language === 'zh' && isMobile;

  const newsLinks = [
    {
      title: t.taxPortal[language],
      url: 'https://www.etax.nat.gov.tw/etwmain',
      description: t.taxPortalDesc[language]
    },
    {
      title: t.cpaUnion[language],
      url: 'https://www.roccpa.org.tw/?AspxAutoDetectCookieSupport=1',
      description: t.cpaUnionDesc[language]
    },
    {
      title: t.nhiPortal[language],
      url: 'https://www.nhi.gov.tw/ch/mp-1.html',
      description: t.nhiPortalDesc[language]
    },
    {
      title: t.laborPortal[language],
      url: 'https://www.bli.gov.tw/',
      description: t.laborPortalDesc[language]
    },
    {
      title: t.taipeiLabor[language],
      url: 'https://bola.gov.taipei/',
      description: t.taipeiLaborDesc[language]
    },
    {
      title: t.commercePortal[language],
      url: 'https://gcis.nat.gov.tw/mainNew/',
      description: t.commercePortalDesc[language]
    }
  ];

  return (
    <div className="pt-16 md:pt-20" data-testid="news-page">
      {/* Hero Section - Tighter on mobile */}
      <section className={`bg-gradient-to-b from-primary/5 to-white ${
        isMobile ? 'py-6' : 'py-16 md:py-24'
      }`}>
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className={`text-center max-w-4xl mx-auto ${isMobile ? '' : ''}`}
          >
            <h1 className={`font-bold font-noto-serif text-primary ${
              isEnMobile ? 'text-2xl mb-3' : isZhMobile ? 'text-3xl mb-3' : 'text-4xl md:text-5xl mb-6'
            }`}>
              {t.pageTitle[language]}
            </h1>
            <p className={`text-text-secondary leading-relaxed font-noto-sans ${
              isMobile ? 'text-sm' : 'text-lg md:text-xl'
            }`}>
              {t.pageSubtitle[language]}
            </p>
          </motion.div>
        </div>
      </section>

      {/* News Links - Tighter cards on mobile */}
      <section className={isMobile ? 'py-6' : 'py-16 md:py-24'}>
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 ${
            isMobile ? 'gap-3' : 'gap-6'
          }`}>
            {newsLinks.map((link, index) => (
              <motion.a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all border border-gray-100 flex flex-col justify-between ${
                  isMobile ? 'p-4' : 'p-6'
                }`}
                data-testid={`news-link-${index}`}
              >
                <div>
                  <div className={`flex items-start justify-between ${isMobile ? 'mb-2' : 'mb-3'}`}>
                    <h3 className={`font-medium font-noto-serif text-primary group-hover:text-secondary transition-colors ${
                      isMobile ? 'text-base leading-snug' : 'text-xl'
                    }`}>
                      {link.title}
                    </h3>
                    <ExternalLink className="text-secondary flex-shrink-0 ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={isMobile ? 16 : 20} />
                  </div>
                  <p className={`text-text-secondary font-noto-sans leading-relaxed ${
                    isMobile ? 'text-xs' : 'text-sm'
                  }`}>{link.description}</p>
                </div>
                <div className={`border-t border-gray-100 ${isMobile ? 'mt-2.5 pt-2.5' : 'mt-4 pt-4'}`}>
                  <span className={`text-secondary font-noto-sans ${isMobile ? 'text-[11px]' : 'text-xs'}`}>{t.clickToVisit[language]}</span>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Info - More compact on mobile */}
      <section className={`bg-background-paper ${isMobile ? 'py-5' : 'py-16'}`}>
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className={`text-text-primary font-noto-sans leading-relaxed ${
              isMobile ? 'text-sm' : ''
            }`}>
              {t.additionalInfo[language]}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default NewsPage;
